import { NgTemplateOutlet } from '@angular/common';
import { Component, DoCheck, Input, IterableDiffer, IterableDiffers } from '@angular/core';
import { AddDomainComponent } from 'components/hosting/company-domains/add-domain.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { CredentialType } from 'enums/CredentialType';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { CredentialRequired } from 'services/credential.service';
import { DomainService, DomainWithDnsInformation } from 'services/domain.service';
import { NavigationService } from 'services/navigation.service';
import { DomainDetailsComponent } from './domain-details.component';
import { Pipes } from 'modules/pipes';
import { RequestRegistrarManagementComponent } from './request-registrar-management.component';

@CredentialRequired(CredentialType.AccessCompanyDomains)
@Component({
	selector: 'company-domains',
	templateUrl: './company-domains.component.html',
	imports: [Layout, ListView, NgTemplateOutlet, DomainDetailsComponent, AddDomainComponent, Pipes]
})
export class CompanyDomainsComponent implements DoCheck {

	private _companyId?: string;
	@Input()
	set companyId(value: string | undefined) {
		this._companyId = value;
		this.getCompanyDomains().then(_ => this.getCompanyProductPlans()); // Ensure domains list is populated before getting product plans, to proper filter used plans
	}
	get companyId() {
		return this._companyId;
	}

	private domainsDiffer?: IterableDiffer<DomainWithDnsInformation>;

	dataProvider: CompanyDataProvider;

	buyDomainTargetPlan?: ProductPlanModel;
	selectedDomain?: DomainWithDnsInformation;
	companyDomains?: DomainWithDnsInformation[];
	domainProductPlans?: ProductPlanModel[];
	requestRegistrarManagementCredential = CredentialType.RequestRegistrarManagement;

	isRegistrantManaged = (domain: DomainWithDnsInformation) => !this.domainService.domainHasPlan(domain);
	isRegistrarManaged = (domain: DomainWithDnsInformation) => this.domainService.domainHasPlan(domain);

	get anyRegistrantManagedDomains() {
		return (this.companyDomains?.filter(domain => this.isRegistrantManaged(domain)).length ?? 0) > 0;
	}
	get anyRegistrarManagedDomains() {
		return (this.companyDomains?.filter(domain => this.isRegistrarManaged(domain)).length ?? 0) > 0;
	}

	constructor(private readonly navigation: NavigationService,
		private readonly domainService: DomainService,
		private readonly iterableDiffers: IterableDiffers,
		dataProviderFactory: CompanyDataProviderFactory) {

		this.dataProvider = dataProviderFactory.getProviderForCurrentScope();
	}

	async getCompanyDomains() {
		if (!this.companyId) return;

		this.companyDomains = await this.dataProvider.getDomains();
		this.domainsDiffer = this.iterableDiffers.find(this.companyDomains).create<DomainWithDnsInformation>();
	}

	hasValidDomainForRegistrarManagement() {
		return this.domainsReadyForRegistrarManagement().length > 0;
	}

	async getCompanyProductPlans() {
		if (!this.companyId) return;
		const domainProductPlans = await this.dataProvider.getProductPlansByTag(ProductPlanTagConstants.domainDk, ProductPlanTagConstants.domainForeign);
		const usedProductPlanIds = this.companyDomains?.filter(domain => domain.productPlanId).map(domain => domain.productPlanId) ?? [];
		this.domainProductPlans = domainProductPlans.filter(pp => !pp.deliveryDate && usedProductPlanIds.indexOf(pp.id) < 0); // Only show unstarted and unlinked plans
	}

	addExistingDomain() {
		this.navigation.openPopup(AddDomainComponent).then(view => {
			view.companyId = this.companyId;
			view.registerNewDomain = false;
		});
	}

	domainsReadyForRegistrarManagement() {
		return this.companyDomains?.filter(domain => this.isRegistrantManaged(domain) && domain.name.isValidDomainDk() && this.domainService.isOurNameserver(domain.nameServer)) ?? [];
	}

	requestRegistrarManagement() {
		this.navigation.openPopup(RequestRegistrarManagementComponent).then(view => {
			const domains = this.domainsReadyForRegistrarManagement();
			view.registrantManagedDomains = domains;
			view.selectedDomains = [...domains];
			view.companyId = this.companyId;
		});
	}

	ngDoCheck() {
		const changes = this.domainsDiffer?.diff(this.companyDomains);
		if (!changes) return;

		changes.forEachAddedItem(record => {
			const addedDomain = record.item;

			if (this.domainProductPlans) {
				const matchingPlanIndex = this.domainProductPlans.findIndex(pp => pp.id == addedDomain.productPlanId);
				if (matchingPlanIndex >= 0) this.domainProductPlans.splice(matchingPlanIndex, 1);
			}
			if (this.buyDomainTargetPlan && this.buyDomainTargetPlan.id == addedDomain.productPlanId) {
				this.buyDomainTargetPlan = undefined;
				this.selectedDomain = addedDomain;
			}
			this.domainService.getDomainStatus(addedDomain);
		});
	}

	getDomainStatusColor(domain: DomainWithDnsInformation) {
		if (!domain.status) return 'color-blue-10';
		if (domain.status.length) return 'color-yellow-50';
		return 'color-green-50';
	}
	getDomainStatusIcon(domain: DomainWithDnsInformation) {
		if (!domain.status) return 'hourglass-half';
		if (domain.status.length) return 'exclamation-triangle';
		return 'square-full';
	}
}
